import { Fragment, useState, useEffect, useRef, useMemo, Suspense } from "react";
import {
  Switch,
  withRouter,
  BrowserRouter,
  useHistory,
} from "react-router-dom";
import Pluralize from "pluralize";
import { useDocumentTitle } from "@hooks/useDocumentTitle";

import { UseRouter } from "@hooks/useRouter";
import ToastPopDown from "@components/modals/toast-popdown-redux";
import SpinnerForContentLoading from "@components/layout/spinner-for-content-loading";
import SuspenseFallback from "@components/layout/SuspenseFallback";
import ProtectedRoute from "@components/shared/protected-route";
import SideBar from "@components/navigation/SideBar";
import Header from "@components/navigation/Header";
// utils
import { BASE_PAGE } from "@constants/index";
import { camelToTitle, snakeToCamel, camelize } from "@utils/string-helpers";
import { getBrowserParams } from "@utils/browser-helpers";
import { store, injectReducers, overrideReducers, restoreStaticReducers } from "@store";
import { updatePageRoute } from "@actions/global";

import { syfBackground } from "@styles/synchrony-core-palette.js";

const styles = {
  dashboardContainer: {
    overflowY: "scroll",
    overflowX: "inherit",
    width: "auto",
    paddingBottom: "60px",
  },
  nonDashboardContainer: {
    overflowX: "hidden",
    overflowY: "inherit",
    width: "90%",
    marginLeft: "0px",
  },
};

/**
 * Dynamically load a feature into the single global store: for non-migrated
 * features, lazy-import their reducer files and inject them under their
 * existing state keys, then run the feature's init side-effect. For migrated
 * features (banners, files, notifications, offers) reducers are already
 * registered as static reducers; we only run the init.
 *
 * This mirrors the previous composeReduxStore() switch statement, but injects
 * into a single global store instead of spinning up a per-route store.
 */
const initFeature = async (
  featureName: string,
  callInitFunc: boolean
): Promise<void> => {
  switch (featureName) {
    case "banners": {
      const { bannersInit } = await import(
        "@features/banners/banner-interface"
      );
      store.dispatch(bannersInit());
      return;
    }
    case "files": {
      // Ensure the static (Sypi) file reducers are active in case the File
      // Manager page overrode these shared keys earlier this session.
      restoreStaticReducers(["fileLocations", "fileAllView", "fileNewView"]);
      const { filesInit } = await import("@features/files/file-interface");
      store.dispatch(filesInit());
      return;
    }
    case "notifications": {
      const { notificationsInit } = await import(
        "@features/notifications/notification-interface"
      );
      store.dispatch(notificationsInit());
      return;
    }
    case "offers": {
      const { promotionsInit } = await import(
        "@features/promotions/promotion-interface"
      );
      store.dispatch(promotionsInit());
      return;
    }
    case "select-clients": {
      const [
        { default: selectClientReducer },
        { default: maintenanceModeReducer },
        { default: DTPReducer },
        { default: DTPSchedulerReducer },
        { selectClientsInit },
      ] = await Promise.all([
        import("@features/select-clients/reducers/selectClientReducer"),
        import("@features/select-clients/reducers/maintenanceModeReducer"),
        import("@features/dtp/reducers/dtpReducer"),
        import("@features/dtp/reducers/dtpSchedulerReducer"),
        import("@features/select-clients/select-client-interface"),
      ]);
      injectReducers({
        selectClients: selectClientReducer,
        maintenanceMode: maintenanceModeReducer,
        DTP: DTPReducer,
        DTPScheduler: DTPSchedulerReducer,
      });
      if (callInitFunc) {
        selectClientsInit(store);
      }
      return;
    }
    case "stores": {
      const [
        { default: storesReducer },
        { default: storeNewViewReducer },
        { default: locationGroupsReducer },
        { default: locationGroupNewViewReducer },
        { default: bulkUploadNewViewReducer },
        { storesInit },
        { locationGroupsInit },
      ] = await Promise.all([
        import("@features/locations/reducers/storesReducer"),
        import("@features/locations/reducers/storeNewViewReducer"),
        import("@features/locations/reducers/locationGroupsReducer"),
        import("@features/locations/reducers/locationGroupNewViewReducer"),
        import("@features/locations/reducers/bulkUploadNewViewReducer"),
        import("@features/locations/store-interface"),
        import("@features/locations/locationGroup-interface"),
      ]);
      injectReducers({
        stores: storesReducer,
        storeNewView: storeNewViewReducer,
        locationGroups: locationGroupsReducer,
        locationGroupNewView: locationGroupNewViewReducer,
        bulkUploadNewView: bulkUploadNewViewReducer,
      });
      storesInit(store);
      locationGroupsInit(store);
      return;
    }
    case "sypi-config": {
      const [
        { default: sypiSchemaReducer },
        { default: sypiNewViewReducer },
        { default: sypiConfigSchedulerReducer },
        { sypiV2Init },
      ] = await Promise.all([
        import("@features/sypi/reducers/sypiSchemaReducer"),
        import("@features/sypi/reducers/sypiNewViewReducer"),
        import("@features/sypi/reducers/sypiConfigSchedulerReducer"),
        import("@features/sypi/sypi-interface"),
      ]);
      injectReducers({
        sypiSchema: sypiSchemaReducer,
        sypiNewView: sypiNewViewReducer,
        sypiConfigScheduler: sypiConfigSchedulerReducer,
      });
      sypiV2Init(store);
      return;
    }
    case "filemanager": {
      // FM3 has its own reducers under the shared file state keys and dispatches
      // LOAD_FILE_LOCATIONS* (not the migrated slices' LOAD_FILES*). Override the
      // keys so those actions are handled; /files restores the static reducers
      // in its own case, so it is unaffected.
      const [
        { default: fileManagerReducer },
        { default: fmAllViewReducer },
        { default: fmNewViewReducer },
        { fileManagerInit },
      ] = await Promise.all([
        import("@features/file-manager/reducers/fileManagerReducer"),
        import("@features/file-manager/reducers/fmAllViewReducer"),
        import("@features/file-manager/reducers/fmNewViewReducer"),
        import("@features/file-manager/fm-interface"),
      ]);
      overrideReducers({
        fileLocations: fileManagerReducer,
        fileAllView: fmAllViewReducer,
        fileNewView: fmNewViewReducer,
      });
      fileManagerInit(store);
      return;
    }
    case "pn-testings": {
      const [
        { default: pnTestingAllViewReducer },
        { default: pnTestingNewViewReducer },
        { pnTestingInit },
      ] = await Promise.all([
        import("@features/pn-testing/reducers/pnTestingAllViewReducer"),
        import("@features/pn-testing/reducers/pnTestingNewViewReducer"),
        import("@features/pn-testing/pnTesting-interface"),
      ]);
      injectReducers({
        pnTestingAllView: pnTestingAllViewReducer,
        pnTestingNewView: pnTestingNewViewReducer,
      });
      pnTestingInit(store);
      return;
    }
    case "build-details": {
      const [
        { default: buildDetailsReducer },
        { defaultInit },
      ] = await Promise.all([
        import("@features/build-details/reducers/buildDetailsReducer"),
        import("@reducers/_utils/default-interface"),
      ]);
      injectReducers({ buildDetails: buildDetailsReducer });
      defaultInit(store);
      return;
    }
    case "c3-clients": {
      const [
        { default: c3ClientsReducer },
        { default: c3ClientGroupsReducer },
        { default: c3ClientsNewViewReducer },
        { default: c3ClientGroupsNewViewReducer },
        { c3Init },
        { defaultInit },
      ] = await Promise.all([
        import("@features/c3/reducers/c3ClientsReducer"),
        import("@features/c3/reducers/c3ClientGroupsReducer"),
        import("@features/c3/reducers/c3ClientsNewViewReducer"),
        import("@features/c3/reducers/c3ClientGroupsNewViewReducer"),
        import("@features/c3/c3-client-interface"),
        import("@reducers/_utils/default-interface"),
      ]);
      injectReducers({
        c3Clients: c3ClientsReducer,
        c3ClientGroups: c3ClientGroupsReducer,
        c3ClientsNewView: c3ClientsNewViewReducer,
        c3ClientGroupsNewView: c3ClientGroupsNewViewReducer,
      });
      c3Init(store);
      defaultInit(store);
      return;
    }
    case "sypi-legacys": {
      const [
        { default: sypiLegacyReducer },
        { sypiLegacyInit },
      ] = await Promise.all([
        import("@features/sypi-legacy/reducers/sypiLegacyReducer"),
        import("@features/sypi-legacy/sypi-legacy-interface"),
      ]);
      injectReducers({ sypiLegacyAllView: sypiLegacyReducer });
      sypiLegacyInit(store);
      return;
    }
    case "schema-management": {
      const [
        { default: schemaReducer },
        { default: schemaConfigSchedulerReducer },
        { default: schemaAllViewReducer },
        { schemaMangementInit },
      ] = await Promise.all([
        import("@features/schema-management/reducers/schemaReducer"),
        import("@features/schema-management/reducers/schemaConfigSchedulerReducer"),
        import("@features/schema-management/reducers/schemaAllViewReducer"),
        import("@features/schema-management/interfaces/schemaManagementInterface"),
      ]);
      injectReducers({
        schema: schemaReducer,
        schemaConfigScheduler: schemaConfigSchedulerReducer,
        schemaAllView: schemaAllViewReducer,
      });
      schemaMangementInit(store);
      return;
    }
    case "dtp": {
      const [
        { default: DTPReducer },
        { default: DTPSchedulerReducer },
        { dtpInit },
      ] = await Promise.all([
        import("@features/dtp/reducers/dtpReducer"),
        import("@features/dtp/reducers/dtpSchedulerReducer"),
        import("@features/dtp/dtpInterface"),
      ]);
      injectReducers({ DTP: DTPReducer, DTPScheduler: DTPSchedulerReducer });
      dtpInit(store);
      return;
    }
    default: {
      const { defaultInit } = await import(
        "@reducers/_utils/default-interface"
      );
      defaultInit(store);
      return;
    }
  }
};

const BaseRenderTemplate = ({
  pageName,
  customStoreName,
  defaultStore,
  callInitFunc,
  searchBrowserParams,
  dynamicTitles,
  clearFormFieldsAction,
  allViewComponent,
  newViewComponent,
  customRoutePaths,
  PageHeader,
  zeroStepAuth,
  oneStepAuth,
  singularPath,
  divId,
  disableNewPage,
  customPath,
  mainContentWrapperStyle,
}: any) => {
  const [ready, setReady] = useState(false);
  const [pathName, setPathName] = useState("");
  const [locationName, setLocationName] = useState("");
  const [searchParams, setSearchParams] = useState("");
  const [scrollPosition, setScrollPosition] = useState(0);

  useDocumentTitle(
    `${camelToTitle(camelize(locationName))} | Control Center | GPShopper`
  );

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      const featureName = defaultStore
        ? ""
        : customStoreName || `${pageName}s`;
      await initFeature(featureName, callInitFunc !== false);
      if (cancelled) return;
      setReady(true);
      setPathName(pageName);
      setLocationName(pageName);
      setSearchParams(
        // @ts-expect-error TypeScript doesn't know that location is a global variable
        searchBrowserParams ? getBrowserParams(location.search) : ""
      );
      // Persisted state is scoped to `sypiNewView`; keep the previous cleanup
      // behavior for other routes to avoid stale rehydration.
      if (
        localStorage.getItem("persist:root") !== null &&
        pageName !== BASE_PAGE.SYPI &&
        pageName !== BASE_PAGE.SCHEMA_MANAGEMENT
      ) {
        localStorage.removeItem("persist:root");
      }
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const MainConterTemplate = () => {
    const history = useHistory();
    const mainWrapperRef = useRef<HTMLDivElement | null>(null);

    let historyListener: (() => void) | null = null;

    useEffect(() => {
      if (dynamicTitles && history) {
        historyListener = history.listen((location: any) => onRouteChange(location));
      }
      return () => {
        if (historyListener) {
          historyListener();
        }
      };
    }, [history]);

    const onRouteChange = (location: Location) => {
      if (!mainWrapperRef || !mainWrapperRef?.current?.scrollTop) return;

      const newLocationName = location.pathname.substring(1);
      const updatedNewLocationName = camelToTitle(
        snakeToCamel(newLocationName)
      );

      setScrollPosition(mainWrapperRef.current.scrollTop);
      setPathName(Pluralize.singular(newLocationName));
      setLocationName(updatedNewLocationName);

      store.dispatch(updatePageRoute(updatedNewLocationName));
    
      if (
        mainWrapperRef &&
        mainWrapperRef.current &&
        mainWrapperRef.current.scrollTop > 0
      ) {
        mainWrapperRef.current.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      }
      if (clearFormFieldsAction && newLocationName.indexOf("_new") === -1) {
        const mainContentWrapper = document.querySelector("#main-content");
    
        if (!mainContentWrapper || !mainContentWrapper.scrollTop) return;

        mainContentWrapper.scrollTop = scrollPosition;
        store.dispatch(clearFormFieldsAction());
      }
    };

    const isDashboardPage = useMemo(() => customPath === "", [customPath]);
  
    return (
      <div
        className="main-content-wrapper hide-scroll-bar"
        id="main-content"
        ref={mainWrapperRef}
        style={{
          ...(isDashboardPage
            ? styles.dashboardContainer
            : styles.nonDashboardContainer),
          ...((oneStepAuth || zeroStepAuth) && { width: "100%" }),
          ...mainContentWrapperStyle,
        }}
      >
        {!oneStepAuth && !zeroStepAuth && <Header />}
        {PageHeader && <PageHeader />}
        <Suspense fallback={<SuspenseFallback />}>
          <Switch>
            <ProtectedRoute
              exact
              path={`/${typeof customPath === "string"
                ? customPath
                : `${pageName}${singularPath ? "" : "s"}`
                }`}
              twoStepAuth={!oneStepAuth && !zeroStepAuth}
              oneStepAuth={oneStepAuth}
              zeroStepAuth={zeroStepAuth}
              component={withRouter(allViewComponent)}
              searchParams={searchParams}
            />
            {disableNewPage ? null : (
              <ProtectedRoute
                path={
                  typeof customPath === "string"
                    ? `/${customPath.substring(0, customPath.length - 1)}_new`
                    : `/${pageName}_new`
                }
                twoStepAuth={!oneStepAuth && !zeroStepAuth}
                oneStepAuth={oneStepAuth}
                zeroStepAuth={zeroStepAuth}
                component={withRouter(newViewComponent)}
                searchParams={searchParams}
              />
            )}
            {customRoutePaths}
          </Switch>
        </Suspense>
      </div>
    );
  };

  const renderContents = () => {
    return (
      <BrowserRouter>
        <div id={`index-${pathName}`}>
          <UseRouter />
          {!defaultStore && (
            <Fragment>
              <ToastPopDown />
              <SpinnerForContentLoading
                useConfigSpinner
                divWithWidthToMatch={divId || pageName}
                topOffset="250px"
              />
            </Fragment>
          )}
          <div
            className="main-wrapper"
            style={{
              backgroundColor: syfBackground,
              width: "100%",
              height: "100vh",
              display: "flex",
              justifyContent: "flex-start",
              margin: "0",
              padding: "0",
            }}
          >
            {!oneStepAuth && !zeroStepAuth && (
              <div className="main-sidebar-wrapper" style={{ width: "18%" }}>
                <SideBar />
              </div>
            )}
            <MainConterTemplate />
          </div>
        </div>
      </BrowserRouter>
    );
  };

  return ready ? renderContents() : null;
};

export default BaseRenderTemplate;
